import { useEffect, useState } from 'react';

/**
 * Reactive `(max-width: 767px)` media-query hook. Mirrors Tailwind's `md`
 * breakpoint (md ≥ 768px), so `useIsMobile() === true` iff the viewport is
 * below md. SSR-safe — returns `false` on the first render when `window` is
 * undefined, then re-evaluates on mount.
 *
 * Use this when you need to *render different React subtrees* on mobile vs
 * desktop (e.g. desktop side-rail vs mobile bottom sheet). Prefer Tailwind
 * `md:` classes for purely visual differences within the same subtree.
 */
export const useIsMobile = (): boolean => {
  const query = '(max-width: 767px)';
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return matches;
};
